import type { ItemFormData } from '@/api/items/adminItemMutations';
import { Button } from '@/components/common/Button';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { Input } from '@/components/common/Input';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { Select } from '@/components/common/Select';
import type { Item } from '@/domain/Item';
import { supplementFormSchema } from '@/validation/supplementFormValidator';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import { useEffect, useMemo, useRef } from 'react';
import { toFormikValidate } from 'zod-formik-adapter';
import { AllergenMultiSelect } from './AllergenMultiSelect';

export interface StrengthEntry {
  id: string;
  strength: string;
  price: string;
  weight: string;
  nrv_percent: string;
  toDelete?: boolean;
}

interface IngredientFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplement?: Item | null;
  mode: 'add' | 'edit';
  onSubmit: (data: ItemFormData) => void;
  onSaveStrength?: (itemId: number, strength: StrengthEntry) => void;
  onDeleteStrength?: (itemId: number, strengthId: number) => void;
  isPending?: boolean;
}

const sourceOptions = [
  { label: 'Compounding', value: 'Compounding' },
  { label: 'Contract', value: 'Contract' },
  { label: 'Over-the-counter', value: 'Over-the-counter' },
];

// Stable placeholder id — Date.now() here retriggered Formik enableReinitialize
// on every render (dialog stays mounted while closed) and overflowed React updates.
const EMPTY_STRENGTH_ID = 'new';
let emptyStrengthSeq = 0;

const emptyStrength = (id: string = EMPTY_STRENGTH_ID): StrengthEntry => ({
  id,
  strength: '',
  price: '',
  weight: '',
  nrv_percent: '',
});

const nextEmptyStrength = (): StrengthEntry =>
  emptyStrength(`new-${++emptyStrengthSeq}`);

const formatOptionalNumber = (
  value: string | number | null | undefined,
): string => {
  if (value === null || value === undefined || value === '') return '';
  return String(value).replace('.', ',');
};

const parseOptionalNumber = (value: string): number | null => {
  const trimmed = value.trim().replace(',', '.');
  if (!trimmed) return null;
  const num = parseFloat(trimmed);
  return Number.isNaN(num) ? null : num;
};

const getInitialValues = (
  supplement: Item | null | undefined,
  isEditMode: boolean,
): {
  name: string;
  description: string;
  source: string;
  function_name: string;
  unrestricted: boolean;
  allergens: Array<string>;
  strengths: Array<StrengthEntry>;
} => {
  if (!isEditMode || !supplement) {
    return {
      name: '',
      description: '',
      source: '',
      function_name: '',
      unrestricted: false,
      allergens: [],
      strengths: [emptyStrength()],
    };
  }
  const loadedStrengths = (supplement.strengths ?? []).map((s) => ({
    id: s.id.toString(),
    strength: s.strength,
    price: String(s.formatted_price).replace('.', ',') || '',
    weight: formatOptionalNumber(s.weight),
    nrv_percent: formatOptionalNumber(s.nrv_percent),
  }));
  return {
    name: supplement.name,
    description: supplement.description,
    source: supplement.source ?? '',
    function_name: supplement.function_name ?? '',
    unrestricted: !!supplement.unrestricted,
    allergens: supplement.allergens ?? [],
    strengths: loadedStrengths.length > 0 ? loadedStrengths : [emptyStrength()],
  };
};

export function IngredientFormDialog({
  open,
  onOpenChange,
  supplement,
  mode,
  onSubmit,
  onSaveStrength,
  onDeleteStrength,
  isPending,
}: IngredientFormDialogProps) {
  const isEditMode = mode === 'edit';
  const itemId = supplement?.id;
  const initialStrengthsRef = useRef<Array<StrengthEntry>>([]);

  const initialValues = useMemo(
    () =>
      open
        ? getInitialValues(supplement ?? null, isEditMode)
        : getInitialValues(null, false),
    [open, supplement, isEditMode],
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validate: toFormikValidate(supplementFormSchema),
    onSubmit: (values) => {
      onSubmit({
        name: values.name,
        description: values.description || undefined,
        source: values.source || undefined,
        function_name: values.function_name || undefined,
        unrestricted: values.unrestricted,
        allergens: values.allergens,
        strengths: values.strengths.map((s) => ({
          id: Number.isNaN(Number(s.id)) ? undefined : Number(s.id),
          strength: s.strength,
          price: parseFloat(s.price.replace(',', '.')) || 0,
          weight: parseOptionalNumber(s.weight),
          nrv_percent: parseOptionalNumber(s.nrv_percent),
          toDelete: s.toDelete,
        })),
      });
    },
  });

  const {
    values,
    setFieldValue,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
  } = formik;
  const strengths = values.strengths;

  useEffect(() => {
    if (open && isEditMode && supplement) {
      initialStrengthsRef.current = getInitialValues(
        supplement,
        true,
      ).strengths;
    } else if (!open) {
      initialStrengthsRef.current = [];
    }
  }, [open, isEditMode, supplement]);

  const sanitizeDecimalInput = (value: string): string => {
    const digitsAndSep = value.replace(/[^\d.,]/g, '');
    const parts = digitsAndSep.split(/[.,]/);
    if (parts.length <= 2) return digitsAndSep;
    const sep = digitsAndSep.includes(',') ? ',' : '.';
    return parts[0] + sep + parts.slice(1).join('');
  };

  const handleInlineSaveStrength = (s: StrengthEntry) => {
    if (itemId && onSaveStrength) {
      onSaveStrength(itemId, s);
    }
  };

  const dialogTitle = isEditMode ? 'Edit Supplement' : 'Add Supplement';
  const submitButtonText = isEditMode ? 'Save Changes' : 'Add Supplement';
  const pendingText = isEditMode ? 'Saving...' : 'Adding...';

  return (
    <DialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      title={dialogTitle}
      maxWidth="4xl"
    >
      {open && (
        <FormikProvider value={formik}>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Input
              id="name"
              name="name"
              label="Name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.name ? errors.name : undefined}
              touched={touched.name}
              className="bg-white"
            />

            <Select
              options={sourceOptions}
              value={values.source}
              onChange={(value) => setFieldValue('source', value)}
              className="bg-white"
              label="Source"
            />

            <Input
              id="description"
              name="description"
              label="Other ingredients / description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              className="bg-white"
            />

            <Input
              id="function_name"
              name="function_name"
              label="Enter supplement function"
              value={values.function_name}
              onChange={handleChange}
              onBlur={handleBlur}
              className="bg-white"
            />

            <AllergenMultiSelect
              value={values.allergens}
              onChange={(allergens) => setFieldValue('allergens', allergens)}
            />

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={values.unrestricted}
                onChange={(e) =>
                  setFieldValue('unrestricted', e.target.checked)
                }
                className="text-primary size-4 rounded border-gray-300 focus:ring-2"
              />
              Check if item is un-restricted
            </label>

            <div className="mt-2">
              <div className="mb-2 grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3">
                <span className="text-sm font-medium">Strength</span>
                <span className="text-sm font-medium">Price</span>
                <span className="text-sm font-medium">Weight (g)</span>
                <span className="text-sm font-medium">%NRV</span>
                <span className="text-sm font-medium">Actions</span>
              </div>
              {typeof errors.strengths === 'string' && (
                <p className="text-destructive mb-1 text-sm">
                  {errors.strengths}
                </p>
              )}
              <FieldArray name="strengths">
                {({ remove }) =>
                  strengths.map((s, index) => {
                    const initial = initialStrengthsRef.current.find(
                      (i) => i.id === s.id,
                    );
                    const isDirty =
                      !initial ||
                      initial.strength !== s.strength ||
                      initial.price !== s.price ||
                      initial.weight !== s.weight ||
                      initial.nrv_percent !== s.nrv_percent;

                    return (
                      <div
                        key={s.id}
                        className="mb-2 grid grid-cols-[1fr_1fr_1fr_1fr_auto] items-center gap-3 border-b border-slate-50 pb-2 last:border-0"
                      >
                        <Input
                          name={`strengths.${index}.strength`}
                          value={s.strength}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="bg-white"
                        />
                        <Input
                          name={`strengths.${index}.price`}
                          value={s.price}
                          onChange={(e) => {
                            setFieldValue(
                              `strengths.${index}.price`,
                              sanitizeDecimalInput(e.target.value),
                            );
                          }}
                          onBlur={handleBlur}
                          inputMode="decimal"
                          className="bg-white"
                        />
                        <Input
                          name={`strengths.${index}.weight`}
                          value={s.weight}
                          onChange={(e) => {
                            setFieldValue(
                              `strengths.${index}.weight`,
                              sanitizeDecimalInput(e.target.value),
                            );
                          }}
                          onBlur={handleBlur}
                          inputMode="decimal"
                          placeholder="0.5"
                          className="bg-white"
                        />
                        <Input
                          name={`strengths.${index}.nrv_percent`}
                          value={s.nrv_percent}
                          onChange={(e) => {
                            setFieldValue(
                              `strengths.${index}.nrv_percent`,
                              sanitizeDecimalInput(e.target.value),
                            );
                          }}
                          onBlur={handleBlur}
                          inputMode="decimal"
                          placeholder="—"
                          className="bg-white"
                        />
                        <div className="flex items-center gap-2">
                          {isEditMode && itemId && (
                            <RelationActionButton
                              type="button"
                              onClick={() => handleInlineSaveStrength(s)}
                              disabled={
                                isPending ||
                                !s.strength.trim() ||
                                !s.price.trim() ||
                                !isDirty
                              }
                            >
                              Save
                            </RelationActionButton>
                          )}
                          <RelationActionButton
                            type="button"
                            onClick={() => {
                              if (
                                itemId &&
                                onDeleteStrength &&
                                !Number.isNaN(Number(s.id))
                              ) {
                                onDeleteStrength(itemId, Number(s.id));
                              }
                              remove(index);
                              if (strengths.length <= 1) {
                                setFieldValue('strengths', [
                                  nextEmptyStrength(),
                                ]);
                              }
                            }}
                            disabled={isPending}
                            className="text-delete"
                          >
                            Delete
                          </RelationActionButton>
                        </div>
                      </div>
                    );
                  })
                }
              </FieldArray>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() =>
                  setFieldValue('strengths', [
                    ...strengths,
                    nextEmptyStrength(),
                  ])
                }
              >
                Add New Strength
              </Button>
            </div>

            <div className="mt-4 flex items-center justify-between gap-4">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={isPending || !values.name.trim()}
              >
                {isPending ? pendingText : submitButtonText}
              </Button>
              <RelationActionButton
                type="button"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </RelationActionButton>
            </div>
          </form>
        </FormikProvider>
      )}
    </DialogWrapper>
  );
}
